import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "../../../components/Select";
import { useToast } from "../../../components/Toast";
import { formatDate } from "../../../lib/format";
import type { ApiError } from "../../../api/client";
import {
  LOT_DOCUMENT_KINDS,
  lotDocumentApi,
  type LotDocumentKind,
  type LotRef,
} from "../../../api/lotDocuments";

const MAX_BYTES = 25 * 1024 * 1024;
const ALLOWED = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

/**
 * Prilaganje dokumenta uz LOT.
 *
 * Fajl ide prvi, red drugi: tabela ne drži dokument bez fajla. Zato se ovde ne može sačuvati zapis pa
 * dodati PDF kasnije — ili je fajl u skladištu, ili dokumenta nema.
 *
 * LOT se traži po oznaci sa deklaracije, jer je to ono što laborant ima u ruci. Priloženi dokument kupac
 * još ne vidi; to je odvojena odluka direktora kvaliteta.
 */
export default function LotDocumentForm({
  onCancel,
  onSaved,
}: {
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = i18n.resolvedLanguage ?? "sr";

  const [term, setTerm] = useState("");
  const [hits, setHits] = useState<LotRef[] | null>(null);
  const [lot, setLot] = useState<LotRef | null>(null);
  const [kind, setKind] = useState<LotDocumentKind>("COA");
  const [issueNumber, setIssueNumber] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  async function findLots() {
    if (!term.trim()) return;
    try {
      setHits(await lotDocumentApi.searchLots(term.trim()));
    } catch (e) {
      toast.error((e as ApiError).message);
      setHits([]);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!lot) {
      toast.error(t("lotDoc.needLot", "Izaberite LOT."));
      return;
    }
    if (!file) {
      toast.error(t("lotDoc.needFile", "Izaberite fajl."));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("lotDoc.fileTooBig", "Fajl je veći od 25 MB."));
      return;
    }
    if (!ALLOWED.includes(file.type)) {
      toast.error(t("lotDoc.fileType", "Dozvoljeni su PDF, JPEG, PNG i WebP."));
      return;
    }
    setSaving(true);
    try {
      const signed = await lotDocumentApi.presign({
        goodsLotId: lot.id,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      const put = await fetch(signed.url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(t("lotDoc.uploadFailed", "Skladište nije primilo fajl."));
      await lotDocumentApi.create({
        goodsLotId: lot.id,
        kind,
        objectKey: signed.objectKey,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        issueNumber: issueNumber.trim() || null,
        validUntil: validUntil || null,
      });
      toast.success(t("lotDoc.saved", "Dokument je priložen i čeka puštanje kupcu."));
      onSaved();
    } catch (e) {
      toast.error((e as ApiError).message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  function kindLabel(k: LotDocumentKind): string {
    if (k === "COA") return t("lotDoc.kind.COA", "Sertifikat analize");
    if (k === "NON_GMO") return t("lotDoc.kind.NON_GMO", "Izjava o NON GMO statusu");
    if (k === "HEALTH_CERTIFICATE") return t("lotDoc.kind.HEALTH_CERTIFICATE", "Zdravstveni sertifikat");
    return t("lotDoc.kind.OTHER", "Drugo");
  }

  return (
    <form className="card fadein" onSubmit={submit}>
      <div className="card-h">
        <strong>{t("lotDoc.newTitle", "Nov dokument uz LOT")}</strong>
        <div className="sub">
          {t(
            "lotDoc.newLead",
            "Priložen dokument kupac još ne vidi. Puštanje kupcu je odvojena odluka direktora kvaliteta.",
          )}
        </div>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {lot ? (
          <div className="toolbar" style={{ gap: 8, alignItems: "center" }}>
            <span className="tag neutral">{lot.designation ?? "—"}</span>
            <span className="muted">{lot.productName ?? ""}</span>
            <button type="button" className="btn ghost sm" onClick={() => setLot(null)}>
              {t("lotDoc.changeLot", "Promeni LOT")}
            </button>
          </div>
        ) : (
          <>
            <div className="toolbar" style={{ gap: 8 }}>
              <input
                className="input"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={t("lotDoc.lotSearch", "oznaka LOT-a ili barkod")}
              />
              <button type="button" className="btn sm" onClick={() => void findLots()}>
                {t("common.search", "Traži")}
              </button>
            </div>
            {hits !== null &&
              (hits.length === 0 ? (
                <div className="empty">{t("lotDoc.noLots", "Nijedan LOT ne odgovara ovoj oznaci.")}</div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>{t("lotDoc.col.lot", "LOT")}</th>
                      <th>{t("lotDoc.col.product", "Proizvod")}</th>
                      <th>{t("lotDoc.col.producedOn", "Proizveden")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {hits.map((h) => (
                      <tr key={h.id}>
                        <td>{h.designation ?? "—"}</td>
                        <td>{h.productName ?? "—"}</td>
                        <td>{formatDate(h.producedOn, lang)}</td>
                        <td>
                          <button type="button" className="btn sm" onClick={() => setLot(h)}>
                            {t("lotDoc.pick", "Izaberi")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
          </>
        )}

        <div className="toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
          <Select
            value={kind}
            onChange={(v) => setKind(v as LotDocumentKind)}
            options={LOT_DOCUMENT_KINDS.map((k) => ({ value: k, label: kindLabel(k) }))}
          />
          <input
            className="input"
            value={issueNumber}
            onChange={(e) => setIssueNumber(e.target.value)}
            placeholder={t("lotDoc.issueNumber", "broj dokumenta")}
          />
          <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {t("lotDoc.validUntil", "Važi do")}
            <input
              className="input"
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
            />
          </label>
        </div>

        <input
          className="input"
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />

        <div className="page-actions">
          <button type="submit" className="btn primary" disabled={saving}>
            {saving ? t("lotDoc.saving", "Prilažem…") : t("lotDoc.save", "Priloži dokument")}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={saving}>
            {t("common.cancel", "Odustani")}
          </button>
        </div>
      </div>
    </form>
  );
}
